/**
 * src/gm/events/event-log.ts — 持久事件流（设计稿 §3.1/§3.2）。
 *
 * 事件流是 UI 的唯一事实源：一切状态变化产出事件 → 持久化 → 外壳订阅/回放。
 * 审计不再是独立日志，而是事件流的一种 kind——单一日志，过滤成视图。
 *
 * 存储：项目段 tp3:events:<projectId>（项目结算/放弃时清理，摘要入全局段）；
 *       全局段 tp3:events:_global（跨项目事件 + 封盘摘要，capped 500）。
 * 逐 token 流式增量不持久化——走 live-bus（见 live-bus.ts）；事件均为低频。
 */

import {
  GLOBAL_EVENTS_KEY,
  GLOBAL_EVENT_CAP,
  PROJECT_EVENT_CAP,
  eventsKey,
} from '../platform/config'
import { readJson, removeKey, writeJson } from '../platform/storage'

export type RunEventKind =
  | 'phase' // 阶段灯迁移
  | 'narration' // 人格化播报（引擎只传话不创作）
  | 'stream-snapshot' // 流式进度节流快照
  | 'audit' // 落盘审计（写前读、写后审计守则）
  | 'warn'
  | 'error'
  | 'gate' // 门禁进入/解除
  | 'wakeup' // 唤醒请求状态迁移

export type GmPhase = 'idle' | 'planning' | 'art' | 'coding' | 'acceptance' | 'review' | 'settling'

export interface RunEvent {
  seq: number // 单调递增序号（同一事件段内）
  ts: string // ISO 8601
  projectId: string | null
  kind: RunEventKind
  payload: Record<string, unknown>
}

type Listener = (e: RunEvent) => void
const listeners = new Set<Listener>()

export function subscribeEvents(cb: Listener): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function fanOut(e: RunEvent): void {
  for (const l of listeners) l(e)
}

function segmentKey(projectId: string | null): string {
  return projectId ? eventsKey(projectId) : GLOBAL_EVENTS_KEY
}

export function replayEvents(projectId: string | null): RunEvent[] {
  const arr = readJson<RunEvent[]>(segmentKey(projectId))
  return Array.isArray(arr) ? arr : []
}

/** 追加事件：持久化 + 广播。返回带 seq/ts 的完整事件。 */
export function appendEvent(
  projectId: string | null,
  kind: RunEventKind,
  payload: Record<string, unknown>,
): RunEvent {
  const key = segmentKey(projectId)
  const arr = replayEvents(projectId)
  const e: RunEvent = {
    seq: (arr.length > 0 ? arr[arr.length - 1].seq : 0) + 1,
    ts: new Date().toISOString(),
    projectId,
    kind,
    payload,
  }
  arr.push(e)
  const cap = projectId === null ? GLOBAL_EVENT_CAP : PROJECT_EVENT_CAP
  if (arr.length > cap) arr.splice(0, arr.length - cap)
  try {
    writeJson(key, arr)
  } catch (err) {
    // 事件流写失败不阻断业务流程（配额压力先在 artifacts/sessions 处爆）
    console.warn('[gm] 事件流写入失败（不阻断流程）：', err)
  }
  fanOut(e)
  return e
}

// ── 便捷产出器（payload 定形） ──────────────────────────────────────────────

export function emitPhase(projectId: string | null, phase: GmPhase, detail?: string): RunEvent {
  return appendEvent(projectId, 'phase', { phase, ...(detail ? { detail } : {}) })
}

export function narrate(projectId: string | null, speaker: string, text: string): RunEvent {
  return appendEvent(projectId, 'narration', { speaker, text })
}

export function audit(projectId: string | null, key: string, fields: string, reason: string): RunEvent {
  return appendEvent(projectId, 'audit', { key, fields, reason })
}

export function warn(projectId: string | null, text: string): RunEvent {
  return appendEvent(projectId, 'warn', { text })
}

export function emitError(projectId: string | null, code: string, text: string): RunEvent {
  return appendEvent(projectId, 'error', { code, text })
}

export function streamSnapshot(
  projectId: string | null,
  stage: string,
  channel: 'content' | 'reasoning',
  received: number,
): RunEvent {
  return appendEvent(projectId, 'stream-snapshot', { stage, channel, received })
}

export type GateDirection = 'enter' | 'exit'

export function gateEvent(
  projectId: string | null,
  gate: string,
  direction: GateDirection,
  extra?: Record<string, unknown>,
): RunEvent {
  return appendEvent(projectId, 'gate', { gate, direction, ...(extra ?? {}) })
}

export function wakeupEvent(
  projectId: string | null,
  requestId: string,
  action: string,
  transition: string,
  extra?: Record<string, unknown>,
): RunEvent {
  return appendEvent(projectId, 'wakeup', { requestId, action, transition, ...(extra ?? {}) })
}

// ── 封盘清理（设计稿 §3.2） ─────────────────────────────────────────────────

/** 项目结算/放弃：摘要入全局段（capped 500），随后清理项目段 */
export function archiveProjectSummary(projectId: string, summary: string): void {
  audit(null, `tp3:events:${projectId}`, summary, '项目封盘：事件段归档摘要')
}

export function clearProjectEvents(projectId: string): void {
  removeKey(eventsKey(projectId))
}
